import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { PermissionEnum } from '@Application/Security/Roles';
import Eyebrow from '@Guidelines/Component/Eyebrow';
import { theme } from '@Guidelines/Theme/theme';
import useSubjectWorkflowRuns from '@UI/Hooks/useSubjectWorkflowRuns';
import PendingRecordingItem from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/PendingRecordingsSection/PendingRecordingItem';
import useBeneficiaryInterviewsList from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import useOutboxItems from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxItems';
import { css } from '@emotion/react';
import * as React from 'react';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;

	& > *:first-child{
		padding-left: 1rem;
	}
`;

const introStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

const listStyle = css`
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto;
	column-gap: 0.875rem;
	margin: 0;
	padding: 0.375rem 1.25rem;
	list-style: none;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
	border: 1.5px solid ${theme.colors.border};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: 1fr;
	}
`;

type Props = {
	readonly subject: SubjectAddress;
};

/**
 * The recordings kept on this device that no preparation still to complete accounts for: captured
 * without a preparation, or for one that has since been generated or abandoned. Renders nothing when
 * there is none, and nothing either until both the server list and the device's own store have loaded
 * (a recording must never flash here just before it fuses with its server row). A recording attached to
 * a preparation still to complete is shown inside that preparation instead.
 */
const PendingRecordingsSection: React.FC<Props> = ({ subject }) => {
	const canWrite = useHasPermission(
		[PermissionEnum.INTERVIEW_WRITE, PermissionEnum.DOCUMENT_GENERATION_LAUNCH],
		'any',
	);
	const localInterviewPort = useLocalInterviewPort();
	const outboxItems = useOutboxItems();
	const { data: preparations } = useSubjectWorkflowRuns(subject);
	const { entries, localInterviews, isServerDataLoaded, isLocalStoreHydrated, error } =
		useBeneficiaryInterviewsList(subject);

	const buildOnDiscarded = React.useCallback(
		(localInterviewId: string) => (): void => {
			void localInterviewPort.remove(localInterviewId);
		},
		[localInterviewPort],
	);

	const outboxItemsById = React.useMemo(
		() => new Map(outboxItems.map((outboxItem) => [outboxItem.id, outboxItem])),
		[outboxItems],
	);

	const pendingEntries = React.useMemo(() => {
		if (preparations === null || !preparations.isFor(subject)) {
			return [];
		}

		return entries.filter((entry) => {
			if (entry.localInterviewId === null || !(entry.isLocal || entry.isProcessing)) {
				return false;
			}

			const preparationId = localInterviews.find(
				(localInterview) => localInterview.id === entry.localInterviewId,
			)?.preparationId;

			return preparationId === undefined || preparations.findById(preparationId)?.isOpen !== true;
		});
	}, [entries, localInterviews, preparations, subject]);

	if (
		!canWrite ||
		error !== null ||
		!isServerDataLoaded ||
		!isLocalStoreHydrated ||
		pendingEntries.length === 0
	) {
		return null;
	}

	return (
		<section css={sectionStyle} aria-label="Enregistrements en attente sur cet appareil">
			<Eyebrow>Enregistrements en attente sur cet appareil</Eyebrow>
			<p css={introStyle}>
				Ces enregistrements ne sont pas encore envoyés et ne font partie d'aucune préparation à
				compléter.
			</p>
			<ul css={listStyle}>
				{pendingEntries.map((entry) => (
					<PendingRecordingItem
						key={entry.key}
						entry={entry}
						subject={subject}
						outboxItemsById={outboxItemsById}
						buildOnDiscarded={buildOnDiscarded}
					/>
				))}
			</ul>
		</section>
	);
};

export default PendingRecordingsSection;
